import React from 'react';
import { Helmet } from 'react-helmet';
import { useColorMode } from '@chakra-ui/react';
import { motion } from 'framer-motion';

const MentionsLegales = () => {
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const textStyle = `leading-relaxed text-justify font-medium ${isDark ? 'text-gray-300' : 'text-[#1B365D]'}`;
    const sectionTitleStyle = `text-xl font-bold uppercase tracking-tight border-b-2 border-yellow-400 pb-2 inline-block mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`;

    return (
        <>
            <Helmet>
                <title>Mentions légales — Hetep Iaout Services</title>
                <meta name="description" content="Consultez les mentions légales du site Hetep Iaout Services : éditeur, hébergeur, propriété intellectuelle, données personnelles et cookies." />
                <link rel="canonical" href="https://www.hisvie.com/mentions-legales/" />
                <meta property="og:title" content="Mentions légales - Hetep Iaout Services" />
                <meta property="og:description" content="Consultez l'intégralité des mentions légales de Hetep Iaout Services." />
                <meta name="description" content="Mentions légales du site hisvie.com — Hetep Iaout Services. Informations sur l'éditeur, l'hébergeur et la politique de confidentialité." />
            </Helmet>

            <div className={`min-h-screen transition-colors duration-500 ${isDark ? 'bg-gray-900 text-gray-100' : 'bg-[#F8FAFC]'}`}>

                {/* Hero Header */}
                <div className={`pt-32 pb-20 px-6 text-center transition-colors duration-500 ${isDark ? 'bg-gray-800' : 'bg-[#1B365D] text-white'}`}>
                    <motion.h1
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl md:text-5xl font-black mb-4 uppercase tracking-wider"
                    >
                        Mentions <span className="text-yellow-400">Légales</span>
                    </motion.h1>
                    <div className="w-24 h-1 bg-yellow-400 mx-auto rounded-full shadow-sm"></div>
                </div>

                {/* Main Content Area */}
                <div className="max-w-5xl mx-auto px-6 py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className={`space-y-12 p-8 md:p-12 rounded-3xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}
                    >

                        {/* Section 1 */}
                        <section>
                            <h2 className={sectionTitleStyle}>1. Informations légales</h2>
                            <div className={`${textStyle} space-y-4`}>
                                <p>
                                    Conformément aux dispositions des articles 6-III et 19 de la loi n° 2004-575 du 21 juin 2004 pour la Confiance dans l'Économie Numérique (LCEN), nous vous informons que le présent site est édité par :
                                </p>
                                <ul className="list-none space-y-1 pl-4 border-l-4 border-yellow-400">
                                    <li><span className="font-bold">Raison sociale :</span> Hetep Iaout Services</li>
                                    <li><span className="font-bold">Forme juridique :</span> SARL</li>
                                    <li><span className="font-bold">SIRET :</span> 493 761 175 00030</li>
                                    <li><span className="font-bold">N° TVA intracommunautaire :</span> FR29 493 761 175</li>
                                    <li><span className="font-bold">Siège social :</span> 8 place Georges Braque, 93120 La Courneuve</li>
                                    <li><span className="font-bold">Téléphone :</span> 01 43 52 64 23</li>
                                    <li><span className="font-bold">Email :</span> his@donnerlavieauxannees.com</li>
                                    <li><span className="font-bold">Directeur de publication :</span> Christian KENNE MAFOUO N GNINTSA</li>
                                </ul>
                                <p>
                                    <span className="font-bold">Hébergeur du site :</span> Google Cloud EMEA Limited (Google Ireland Limited) — Gordon House, Barrow Street, Dublin 4, Irlande — <a href="https://cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">cloud.google.com</a>
                                </p>
                            </div>
                        </section>

                        {/* Section 2 */}
                        <section>
                            <h2 className={sectionTitleStyle}>2. Limitation de responsabilité</h2>
                            <p className={textStyle}>
                                Vous utilisez le site « www.hisvie.com » sous votre seule et entière responsabilité. Hetep Iaout Services ne pourra être tenue pour responsable des dommages directs ou indirects, tels que, notamment, préjudice matériel, pertes de données ou de programme, préjudice financier, résultant de l'utilisation de ce site ou de sites qui lui sont liés.
                            </p>
                        </section>

                        {/* Section 3 */}
                        <section>
                            <h2 className={sectionTitleStyle}>3. Protection des données personnelles (RGPD)</h2>
                            <div className={`${textStyle} space-y-4`}>
                                <p>
                                    Les traitements de données à caractère personnel réalisés à partir du site « www.hisvie.com » sont effectués en conformité avec le Règlement Général sur la Protection des Données (RGPD — Règlement UE 2016/679 du 27 avril 2016) et la loi n° 78-17 du 6 janvier 1978 modifiée relative à l'informatique, aux fichiers et aux libertés.
                                </p>
                                <p>
                                    Les informations que vous communiquez via les formulaires présents sur le site (contact, devis, recrutement) sont nécessaires pour répondre à votre demande et sont destinées à Hetep Iaout Services, responsable du traitement, à des fins de gestion administrative et commerciale. Ces données ne sont pas cédées à des tiers.
                                </p>
                                <p>
                                    Conformément au RGPD, vous disposez des droits suivants sur vos données personnelles :
                                </p>
                                <ul className="list-disc pl-6 space-y-1">
                                    <li>Droit d'accès et d'information</li>
                                    <li>Droit de rectification</li>
                                    <li>Droit à l'effacement (« droit à l'oubli »)</li>
                                    <li>Droit à la limitation du traitement</li>
                                    <li>Droit d'opposition</li>
                                    <li>Droit à la portabilité des données</li>
                                </ul>
                                <p>
                                    Pour exercer ces droits, adressez votre demande par courrier accompagné d'une copie d'un titre d'identité à :<br />
                                    <span className="font-bold">Hetep Iaout Services — 8 place Georges Braque, 93120 La Courneuve</span><br />
                                    ou par email : <a href="mailto:his@donnerlavieauxannees.com" className="text-blue-600 underline">his@donnerlavieauxannees.com</a>
                                </p>
                                <p>
                                    Vous disposez également du droit d'introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">www.cnil.fr</a>.
                                </p>
                            </div>
                        </section>

                        {/* Section 4 */}
                        <section>
                            <h2 className={sectionTitleStyle}>4. Cookies et mesure d'audience</h2>
                            <div className={`${textStyle} space-y-4`}>
                                <p>
                                    Lors de votre visite sur le site « www.hisvie.com », des cookies peuvent être déposés sur votre navigateur. Un cookie est un petit fichier texte qui ne permet pas d'identifier personnellement l'utilisateur, mais enregistre des informations relatives à sa navigation.
                                </p>
                                <p>
                                    Le site utilise <span className="font-bold">Google Analytics 4</span> (Google Ireland Limited) à des fins de mesure d'audience et d'optimisation de la navigation. Cet outil collecte des données anonymisées (type de navigateur, pages visitées, durée de session, etc.) et peut déposer des cookies analytiques sur votre appareil.
                                </p>
                                <p>
                                    Conformément au RGPD et aux recommandations de la CNIL, vous pouvez refuser ou supprimer ces cookies à tout moment en paramétrant votre navigateur. Pour en savoir plus : <a href="https://www.cnil.fr/fr/cookies-et-autres-traceurs" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">www.cnil.fr</a>.
                                </p>
                            </div>
                        </section>

                        {/* Section 5 */}
                        <section>
                            <h2 className={sectionTitleStyle}>5. Propriété Intellectuelle</h2>
                            <div className={`${textStyle} space-y-4`}>
                                <p>
                                    La structure générale ainsi que les logiciels, textes, images animées ou non, son savoir-faire et tous les autres éléments composant le site sont la propriété exclusive de Hetep Iaout Services.
                                </p>
                                <p>
                                    Toute représentation totale ou partielle de ce site par quelque personne que ce soit, sans l'autorisation expresse de Hetep Iaout Services est interdite et constituerait une contrefaçon sanctionnée par les articles L. 335-2 et suivants du Code de la Propriété Intellectuelle.
                                </p>
                                <p>
                                    La marque Hetep Iaout Services ainsi que les logos figurant sur le site sont des marques protégées. Toute reproduction totale ou partielle de ces marques ou de ces logos sans l'autorisation expresse de Hetep Iaout Services est prohibée au sens du Code de la Propriété Intellectuelle.
                                </p>
                                <p className="font-bold pt-4 text-[#1B365D] dark:text-yellow-400">
                                    Pour tout litige en relation avec l'utilisation du site « www.hisvie.com », l'utilisateur reconnaît la compétence exclusive des tribunaux compétents de Paris.
                                </p>
                            </div>
                        </section>

                        {/* Section 6 */}
                        <section>
                            <h2 className={sectionTitleStyle}>6. Mise à jour</h2>
                            <p className={textStyle}>
                                Hetep Iaout Services se réserve le droit de modifier et de mettre à jour, sans préavis, les présentes mentions légales. L'ensemble de ces modifications s'impose aux utilisateurs qui sont invités à les consulter régulièrement.
                            </p>
                            <p className={`${textStyle} mt-3 text-sm opacity-70`}>
                                Dernière mise à jour : juin 2026
                            </p>
                        </section>

                    </motion.div>

                    <footer className="mt-12 text-center text-sm opacity-60 italic text-gray-500">
                        Nous remercions les utilisateurs du site de nous faire part d'éventuelles omissions ou erreurs en adressant un message à partir de la rubrique contact.
                    </footer>
                </div>
            </div>
        </>
    );
};

export default MentionsLegales;
